
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/hooks/useAuth";

const signInSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  fullName: z.string().optional(),
});


export function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const currentSchema = signInSchema;
  
  const form = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(currentSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });


  async function onSubmit(values: z.infer<typeof signInSchema>) {
    setIsLoading(true);
    try {
      await login({
        email: values.email,
        password: values.password,
      });
      toast.success("Welcome back!");
      // Navigation is handled by AuthContext
    } catch (error: any) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left Side - Brand Logo */}
      <div className="hidden lg:flex lg:w-1/2 bg-figma-primary items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-figma-primary/95 via-figma-primary to-figma-primary"></div>
        <div className="relative z-10 flex flex-col items-center px-8">
          {/* TendencIA Logo */}
          <div className="mb-8 p-6 bg-white/10 rounded-3xl backdrop-blur-sm border border-white/20">
            <BrandLogo size="xl" />
          </div>
          <h1 className="text-white text-4xl font-bold text-center max-w-md mb-4">
            TendencIA Intern System
          </h1>
          <p className="text-white/80 text-xl text-center max-w-md leading-relaxed">
            Sistema profesional de gestión y control de costos de IA
          </p>
          <div className="mt-8 flex items-center gap-2 text-white/60">
            <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Sistema Activo</span>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-gray-100 relative">
        <div className="absolute inset-0 bg-tech-gradient opacity-30"></div>
        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="mb-4 p-4 bg-figma-primary rounded-2xl">
              <BrandLogo size="lg" />
            </div>
            <h1 className="text-2xl font-bold text-figma-primary text-center">
              TendencIA Intern System
            </h1>
          </div>

          {/* Auth Card */}
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-glass border border-figma-primary/10 bg-gradient-to-br from-white/90 to-white/70">
            <h2 className="mb-6 text-2xl font-bold text-figma-primary text-center">
              Bienvenido
            </h2>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-figma-primary font-medium">Correo Electrónico</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="ejemplo@tendencia.ia" 
                          {...field}
                          className="h-12 rounded-figma-input border-figma-primary/10 focus:border-figma-primary/20 focus:ring-figma-primary/20 bg-white/50 backdrop-blur-sm focus:bg-white/70 transition-all hover:bg-white/60 text-figma-primary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-figma-primary font-medium">Contraseña</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="••••••••••••••••"
                          {...field}
                          className="h-12 rounded-figma-input border-figma-primary/10 focus:border-figma-primary/20 focus:ring-figma-primary/20 bg-white/50 backdrop-blur-sm focus:bg-white/70 transition-all hover:bg-white/60 text-figma-primary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-figma-primary hover:bg-figma-primary/90 text-white font-semibold rounded-figma-button transition-colors shadow-figma hover:shadow-2xl" 
                  disabled={isLoading}
                >
                  {isLoading ? "Cargando..." : "Iniciar Sesión"}
                </Button>
              </form>
            </Form>

          </div>
        </div>
      </div>
    </div>
  );
}
